import React from "react";
import { Navigate } from "react-router-dom";

import { Tray, TrayActions, TrayCell, TrayCells, TrayHeader } from "../../../probate/components/tray";
import { getFormattedDate } from "../../../utils/textUtils";
import ScrollToTop from "../../../components/utils/ScrollToTop";
import { useRefreshDashboard, useSelectedCase } from "../utils/hooks";
import ButtonLink from "./ButtonLink";
import ProbateLayout from "./ProbateLayout";

const notificationRow = (notification, caseReference) => ({
  key: `notification-${notification.id}`,
  title: notification.subject,
  href: `/dashboard/${caseReference}/messages/${notification.id}`,
  unread: notification.status !== "read",
  status: notification.status !== "read" ? "New" : undefined,
  date: notification.created_at,
});

// No status for a complete enquiry in Messages
export const enquiryStatus = {
  available: "Reply needed",
  available_to_read: "New",
};

// The submission covers a dashboard reply the staff app hasn't yet added to the conversation
const clientReplied = (enquiry) =>
  !!enquiry.configuration?.conversation?.some((update) => update.type === "update_from_client") ||
  !!enquiry.submission?.enquiry_update?.client_reply;

// An enquiry the client never replied to stays in Messages
export const isCompletedTask = (doable) =>
  doable.status === "complete" && (doable.doable_type !== "enquiry" || clientReplied(doable));

// One waiting for a reply is in Things to do instead
const isEnquiryMessage = (doable) =>
  doable.doable_type === "enquiry" && doable.status !== "available" && !isCompletedTask(doable);

const enquiryRow = (enquiry) => ({
  key: `enquiry-${enquiry.id}`,
  title: enquiry.title,
  href: `/dashboard/doable/${enquiry.id}`,
  unread: enquiry.status !== "complete",
  status: enquiryStatus[enquiry.status],
  date: enquiry.status === "complete" && enquiry.completed_at ? enquiry.completed_at : enquiry.created_at,
});

export const messageRows = (selectedCase) =>
  [
    ...(selectedCase.notifications ?? []).map((n) => notificationRow(n, selectedCase.case_reference)),
    ...selectedCase.doables.filter(isEnquiryMessage).map(enquiryRow),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

export const MessageCells = ({ rows }) => (
  <TrayCells>
    {rows.map((row) => (
      <TrayCell
        key={row.key}
        title={row.title}
        href={row.href}
        status={row.status}
        details={row.date && getFormattedDate(row.date)}
      />
    ))}
  </TrayCells>
);

const ProbateMessages = ({ selectedCase }) => {
  const unread = messageRows(selectedCase).filter((row) => row.unread);

  if (unread.length === 0) {
    return null;
  }

  return (
    <Tray variant="card" headingId="messages">
      <TrayHeader title="Unread messages" count={unread.length} />
      <MessageCells rows={unread} />
      <TrayActions>
        <ButtonLink to={`/dashboard/${selectedCase.case_reference}/messages`}>All messages</ButtonLink>
      </TrayActions>
    </Tray>
  );
};

export default ProbateMessages;

export const ProbateMessagesPage = () => {
  const selectedCase = useSelectedCase({ caseScreen: "messages" });
  // A message's page marks it read, and its back link comes here
  useRefreshDashboard({ staleTime: 0 });

  if (!selectedCase) {
    return null;
  }

  if (selectedCase.case_type !== "probate") {
    return <Navigate to={`/dashboard/${selectedCase.case_reference}`} replace />;
  }

  return (
    <ProbateLayout caseReference={selectedCase.case_reference}>
      <ScrollToTop />
      <Tray variant="card">
        <TrayHeader title="Messages" />
        <MessageCells rows={messageRows(selectedCase)} />
      </Tray>
    </ProbateLayout>
  );
};
